import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Tabs, Tab } from '@blueprintjs/core'
import { getVisibleTabs, getResolvedSelectedTabId } from './utils'

/**
 * Generic page-level tab bar built on top of Blueprint `Tabs`.
 * Renders a horizontal list of tabs from a plain data array, making it
 * reusable across any feature page without wiring Blueprint `Tab` elements
 * directly in the consumer.
 * @component
 * @param {object}   props
 * @param {string}   props.id            - Unique HTML id forwarded to Blueprint `Tabs`.
 * @param {string}   props.selectedTabId - Id of the currently active tab.
 * @param {Function} props.onChange      - Callback invoked with the new tab id when the user switches tabs.
 * @param {Array<{id: string, title: string, permissions?: {anyOf?: string[], allOf?: string[]}}>} props.tabs - Ordered list of tab descriptors.
 * @param {object}   [props.permissionContext] - Optional context object with `permissions` array used for filtering.
 * @param {string}   [props.className]   - Optional extra CSS class applied to the `Tabs` root element.
 * @returns {JSX.Element}
 */
const PageTabs = ({
    id,
    selectedTabId,
    onChange,
    tabs,
    permissionContext = null,
    className = '',
}) => {
    const visibleTabs = useMemo(() => getVisibleTabs(tabs, permissionContext), [
        tabs,
        permissionContext,
    ])
    const resolvedSelectedTabId = getResolvedSelectedTabId(
        visibleTabs,
        selectedTabId
    )

    return (
        <Tabs
            id={id}
            selectedTabId={resolvedSelectedTabId}
            onChange={onChange}
            className={className}
        >
            {visibleTabs.map((tab) => (
                <Tab key={tab.id} id={tab.id} title={tab.title} panel={null} />
            ))}
        </Tabs>
    )
}

PageTabs.propTypes = {
    id: PropTypes.string.isRequired,
    selectedTabId: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    tabs: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            title: PropTypes.string.isRequired,
            permissions: PropTypes.shape({
                anyOf: PropTypes.arrayOf(PropTypes.string),
                allOf: PropTypes.arrayOf(PropTypes.string),
            }),
        })
    ).isRequired,
    permissionContext: PropTypes.shape({
        permissions: PropTypes.arrayOf(PropTypes.string),
    }),
    className: PropTypes.string,
}

export default PageTabs
